import { styled } from '@mui/material/styles';

/**
 * Wrapper für die FullCalendar-Komponente. Wichtige Aufgaben:
 *
 * 1. **Höhenkette intakt halten** — FullCalendar mit `height='100%'` braucht
 *    ALLE Container von hier bis zur Wurzel mit explizit gesetzter Höhe.
 *    Wir setzen `height:100%` und `minHeight:0`, damit der Wrapper in einem
 *    Flex-Layout korrekt schrumpft statt seinen Inhalt zu strecken.
 * 2. **Theme-passendes Styling** für Tag-/Slot-Linien, Header und Buttons.
 * 3. **Keine Override der Event-Farben** — die werden pro Event aus
 *    `_connection.color` abgeleitet und inline gesetzt; ein globaler
 *    `.fc-event { background: primary }` würde das überstimmen.
 */
export const CalendarWrapper = styled('div')(({ theme }) => ({
  height: '100%',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  background: theme.palette.background.paper,
  color: theme.palette.text.primary,
  fontFamily: theme.typography.fontFamily,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',

  // Innerer FullCalendar-Container füllt den Wrapper.
  '& .fc': {
    flex: 1,
    minHeight: 0,
    height: '100%',
  },

  // Header / Toolbar
  '& .fc-header-toolbar': {
    margin: 0,
    padding: theme.spacing(1, 1.5),
    background: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  '& .fc-toolbar-title': {
    fontSize: '1.05rem',
    fontWeight: 600,
  },
  '& .fc-button': {
    background: theme.palette.action.hover,
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    textTransform: 'none',
    boxShadow: 'none',
    fontSize: '0.85rem',
    padding: theme.spacing(0.5, 1.25),
    '&:hover': {
      background: theme.palette.action.selected,
      borderColor: theme.palette.divider,
      color: theme.palette.text.primary,
    },
    '&:focus': { boxShadow: 'none' },
  },
  '& .fc-button-primary:not(:disabled).fc-button-active, & .fc-button-primary:not(:disabled):active':
    {
      background: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },

  // Day-/Time-Grid Linien
  '& .fc-scrollgrid, & .fc-scrollgrid td, & .fc-scrollgrid th': {
    borderColor: theme.palette.divider,
  },
  '& .fc-timegrid-slot, & .fc-daygrid-day, & .fc-col-header-cell': {
    borderColor: theme.palette.divider,
  },

  // Heutiger Tag dezent
  '& .fc-day-today': {
    background:
      theme.palette.mode === 'dark'
        ? 'rgba(31, 111, 235, 0.10)'
        : 'rgba(31, 111, 235, 0.06)',
  },

  // Now-Indicator etwas auffälliger
  '& .fc-timegrid-now-indicator-line': {
    borderColor: theme.palette.error.main,
    borderWidth: 2,
  },
  '& .fc-timegrid-now-indicator-arrow': {
    borderColor: theme.palette.error.main,
  },

  // Event-Optik (Farbe kommt pro Event inline)
  '& .fc-event': {
    border: 'none',
    borderRadius: 4,
    padding: '1px 4px',
    fontSize: '0.78rem',
    cursor: 'pointer',
  },
  '& .fc-event-title': { fontWeight: 500 },
  '& .fc-event-time': { fontWeight: 400, opacity: 0.85 },
}));
